import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Autocomplete, Box, Chip, CircularProgress, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CloseOutlined } from '@mui/icons-material';
import { updateKnowledgeBaseAsync, getKnowledgeBasesAsync } from '../../../../application/features/knowledgeBase/knowledgeBaseThunk';
import { useAppDispatch, useAppSelector } from '../../../../application/store/useStore';
import { toast } from 'react-toastify';
import { useCompanyContext } from '../../../../application/context/CompanyContext';
import { getAllExpertisesAsync } from '../../../../application/features/expertise/expertiseThunk';
export const EditKnowledgeBaseDialog = ({ onClose, openDialog, knowledgeBase }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [isUpdating, setIsUpdating] = React.useState(false);
    const [title, setTitle] = React.useState('');
    const [selectedExpertises, setSelectedExpertises] = React.useState([]);
    const [selectedSubExpertises, setSelectedSubExpertises] = React.useState([]);
    const dispatch = useAppDispatch();
    const { selectedCompanyId } = useCompanyContext();
    const { expertises } = useAppSelector((state) => state.expertise);
    React.useEffect(() => {
        if (openDialog && expertises.length === 0) {
            dispatch(getAllExpertisesAsync());
        }
    }, [openDialog, dispatch, expertises.length]);
    React.useEffect(() => {
        if (openDialog && knowledgeBase) {
            // Set title from knowledge base
            setTitle(knowledgeBase.title || '');
            // Set selected expertises
            if (knowledgeBase.expertiseIdList && expertises.length > 0) {
                const selectedExps = knowledgeBase.expertiseIdList
                    .map((expMapping) => {
                    return expertises.find((e) => e.id === expMapping.expertiseId);
                })
                    .filter(Boolean);
                setSelectedExpertises(selectedExps);
                // Set selected sub-expertises
                const allSubExps = [];
                knowledgeBase.expertiseIdList.forEach((expMapping) => {
                    const expertise = expertises.find((e) => e.id === expMapping.expertiseId);
                    if (expertise && expMapping.subExpertiseIds) {
                        expMapping.subExpertiseIds.forEach((subId) => {
                            const subExp = expertise.subExpertise?.find((se) => se.id === subId);
                            if (subExp) {
                                allSubExps.push(subExp);
                            }
                        });
                    }
                });
                setSelectedSubExpertises(allSubExps);
            }
        }
    }, [openDialog, knowledgeBase, expertises]);
    React.useEffect(() => {
        if (!openDialog) {
            setTitle('');
            setSelectedExpertises([]);
            setSelectedSubExpertises([]);
        }
    }, [openDialog]);
    const onSubmit = async () => {
        if (!title.trim()) {
            toast.error(t('titleRequired') || 'Please enter title');
            return;
        }
        if (selectedExpertises.length === 0) {
            toast.error(t('expertiseRequired') || 'Please select at least one expertise');
            return;
        }
        if (!knowledgeBase?.id) {
            return;
        }
        setIsUpdating(true);
        // Build expertiseIdList from selected expertises and sub-expertises
        const expertiseIdList = selectedExpertises.map((exp) => {
            // Get sub-expertise IDs that belong to this expertise
            const subExpertiseIds = selectedSubExpertises
                .filter((subExp) => exp.subExpertise?.some((se) => se.id === subExp.id))
                .map((subExp) => subExp.id);
            return {
                expertiseId: exp.id,
                subExpertiseIds
            };
        });
        const updateData = {
            title,
            expertiseIdList
        };
        dispatch(updateKnowledgeBaseAsync({ id: knowledgeBase.id, data: updateData }))
            .unwrap()
            .then((response) => {
            toast.success(t('knowledgeBaseUpdateSuccess') || 'Knowledge base updated successfully');
            if (selectedCompanyId) {
                dispatch(getKnowledgeBasesAsync(selectedCompanyId));
            }
            onClose();
        })
            .catch((error) => {
            toast.error(t('knowledgeBaseUpdateFail') || 'Failed to update knowledge base');
        })
            .finally(() => {
            setIsUpdating(false);
        });
    };
    // Get all available sub-expertises from selected expertises
    const availableSubExpertises = React.useMemo(() => {
        const subExps = [];
        selectedExpertises.forEach((exp) => {
            if (exp.subExpertise && exp.subExpertise.length > 0) {
                exp.subExpertise.forEach((se) => {
                    subExps.push({ ...se, parentExpertiseName: exp.name });
                });
            }
        });
        return subExps;
    }, [selectedExpertises]);
    return (_jsxs(Dialog, { maxWidth: 'sm', fullWidth: true, sx: {
            '& .MuiDialog-paper': {
                overflow: 'hidden',
                backgroundColor: theme.palette.background.paper
            }
        }, open: openDialog, onClose: onClose, children: [_jsxs(DialogTitle, { sx: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.divider}`
                }, children: [_jsx(Typography, { variant: 'h6', children: t('editKnowledgeBase') || 'Edit Knowledge Base' }), _jsx(IconButton, { "aria-label": 'close', onClick: onClose, sx: {
                            color: theme.palette.text.primary
                        }, children: _jsx(CloseOutlined, {}) })] }), _jsx(DialogContent, { sx: {
                    paddingTop: '24px !important',
                    backgroundColor: theme.palette.background.paper
                }, children: _jsx(Box, { display: 'flex', flexDirection: 'column', gap: 3, children: _jsxs(Box, { children: [_jsx(Typography, { variant: 'subtitle2', color: 'primary', mb: 1.5, children: t('documentDetails') || 'Document Details' }), _jsx(TextField, { fullWidth: true, label: t('title') || 'Title', value: title, onChange: (e) => setTitle(e.target.value), required: true, sx: { mb: 2 } }), _jsx(Autocomplete, { multiple: true, value: selectedExpertises, options: expertises, getOptionLabel: (option) => option.name || '', onChange: (_, value) => {
                                    setSelectedExpertises(value);
                                    // Filter out sub-expertises that don't belong to newly selected expertises
                                    setSelectedSubExpertises((prev) => prev.filter((subExp) => value.some((exp) => exp.subExpertise?.some((se) => se.id === subExp.id))));
                                }, renderInput: (params) => (_jsx(TextField, { ...params, label: t('category') || 'Category', placeholder: selectedExpertises.length === 0
                                        ? t('selectCategory') || 'Select categories'
                                        : '', required: true })), renderTags: (value, getTagProps) => value.map((option, index) => (_createElement(Chip, { label: option.name, ...getTagProps({ index }), key: option.id, size: 'small' }))), sx: { mb: 2 } }), selectedExpertises.length > 0 && (_jsx(Autocomplete, { multiple: true, value: selectedSubExpertises, options: availableSubExpertises, groupBy: (option) => option.parentExpertiseName, getOptionLabel: (option) => option.name || '', onChange: (_, value) => setSelectedSubExpertises(value), renderInput: (params) => (_jsx(TextField, { ...params, label: t('subCategory') || 'Subcategory', placeholder: selectedSubExpertises.length === 0
                                        ? t('selectSubcategory') || 'Select subcategories'
                                        : '' })), renderTags: (value, getTagProps) => value.map((option, index) => (_createElement(Chip, { label: option.name, ...getTagProps({ index }), key: option.id, size: 'small' }))) }))] }) }) }), _jsxs(DialogActions, { sx: {
                    padding: 2,
                    backgroundColor: theme.palette.background.paper,
                    borderTop: `1px solid ${theme.palette.divider}`
                }, children: [_jsx(Button, { onClick: onClose, variant: 'outlined', children: t('cancel') }), _jsx(Button, { onClick: onSubmit, variant: 'contained', disabled: isUpdating, startIcon: isUpdating ? _jsx(CircularProgress, { size: 16 }) : null, children: isUpdating ? t('updating') || 'Updating...' : t('update') || 'Update' })] })] }));
};
export default EditKnowledgeBaseDialog;
